/**
 * A wish in words, read: what Arrange's third question comes to.
 *
 * The words are read by the same route the lab's Ask box uses (`/theory/ask`), against the
 * settings Arrange chose for the players named. What comes back is one of three things: a
 * question, where the words can be read two ways, which is shown with its answers and nothing
 * is arranged until one is picked; an answer, which is handed to the page to arrange; or a
 * failure to read them, which is said and arranges nothing. An answer may also say what of the
 * wish could not be done (`unmet`): the rest is arranged all the same.
 *
 * The page stays live while the words are read, so the answer is handed to the page as it is
 * when it arrives, and one that arrives after Stop, or after the words were asked again, is
 * dropped.
 */

import { useEffect, useRef, useState } from "react";
import type { AskAnswered, AskPayload, AskQuestion, AskScore } from "@app/shared-types";
import { api } from "../api/client";
import { type Board, settingsOf } from "./ask-run";

/** What reading the words came to, beside the arranging itself. */
export interface WishSaid {
  /** What of the wish could not be done, in the reader's own sentence. */
  readonly unmet: string | null;
  /** What the page has to say of the answer: why nothing is arranged for it, or what it did. */
  readonly note: string | null;
  /** Why the words could not be read at all. */
  readonly failed: string | null;
}

export interface Wish {
  readonly reading: boolean;
  readonly question: AskQuestion | null;
  readonly said: WishSaid | null;
  /** Read `words` against `sent`, in a `score` of `total` bars; `answered` answers a question. */
  ask(
    words: string,
    sent: Board,
    score: AskScore | null,
    total: number | null,
    answered: AskAnswered | null,
  ): void;
  /** Drop the reading in progress. */
  stop(): void;
  /** Forget the last reading: its question and what it said. */
  clear(): void;
}

/**
 * `onAnswer` is handed each answer with the settings it was read against, arranges it, and
 * returns why it could not where it could not.
 */
export function useWish(onAnswer: (answer: AskPayload, sent: Board) => string | null): Wish {
  const [reading, setReading] = useState(false);
  const [question, setQuestion] = useState<AskQuestion | null>(null);
  const [said, setSaid] = useState<WishSaid | null>(null);
  const latest = useRef(onAnswer);
  latest.current = onAnswer;
  // Each reading gets a number, checked before its answer touches the page.
  const generation = useRef(0);
  useEffect(
    () => () => {
      generation.current += 1;
    },
    [],
  );

  const read: Wish["ask"] = async (words, sent, score, total, answered) => {
    generation.current += 1;
    const mine = generation.current;
    setReading(true);
    setSaid(null);
    setQuestion(null);
    try {
      const answer = await api.ask({
        words,
        roster: [...sent.roster],
        settings: settingsOf(sent, total),
        score,
        answered,
      });
      if (generation.current !== mine) return;
      if (answer.question !== null) {
        setQuestion(answer.question);
      } else {
        const note = latest.current(answer, sent);
        setSaid({ unmet: answer.unmet, note, failed: null });
      }
    } catch (failed) {
      if (generation.current !== mine) return;
      const reason = failed instanceof Error ? failed.message : String(failed);
      setSaid({ unmet: null, note: null, failed: reason });
    }
    if (generation.current === mine) setReading(false);
  };

  const stop = (): void => {
    generation.current += 1;
    setReading(false);
  };

  return {
    reading,
    question,
    said,
    ask: (...asked) => void read(...asked),
    stop,
    clear: () => {
      stop();
      setQuestion(null);
      setSaid(null);
    },
  };
}
